import type { StoryCostEvent } from "@/lib/cost/storyCostEvents";

export interface CostPerItemResult {
  totalCostUsd: number;
  distinctItems: number;
  avgCostPerItem: number;
  stories: number;
  bugfixes: number;
  sessionFallbackPct: number;
}

/**
 * Heuristic: classify a work_item_ref as bugfix.
 *
 * story_cost_summary events lack work_type (D-S2.1). Until the server
 * provides it, we classify by ref pattern. Today ~100% are stories.
 */
export function isBugfixRef(ref: string): boolean {
  const lower = ref.toLowerCase();
  return (
    lower.startsWith("bugfix/") ||
    lower.startsWith("hotfix/") ||
    lower.startsWith("fix/")
  );
}

/**
 * Compute cost per delivered item from story_cost_summary events.
 *
 * - Sum cost_usd across all events
 * - Count distinct refs (work items)
 * - Split story/bugfix via isBugfixRef heuristic
 * - Compute session_fallback percentage (events with no date)
 */
export function computeCostPerItem(events: StoryCostEvent[]): CostPerItemResult {
  if (events.length === 0) {
    return {
      totalCostUsd: 0,
      distinctItems: 0,
      avgCostPerItem: 0,
      stories: 0,
      bugfixes: 0,
      sessionFallbackPct: 0,
    };
  }

  let totalCostUsd = 0;
  const refs = new Set<string>();
  const bugfixRefs = new Set<string>();
  let fallbackCount = 0;

  for (const e of events) {
    totalCostUsd += e.costUsd;
    refs.add(e.ref);
    if (isBugfixRef(e.ref)) {
      bugfixRefs.add(e.ref);
    }
    if (e.sessionId && !e.date) {
      fallbackCount++;
    }
  }

  const distinctItems = refs.size;
  const bugfixes = bugfixRefs.size;
  const stories = distinctItems - bugfixes;
  const avgCostPerItem = distinctItems > 0 ? totalCostUsd / distinctItems : 0;
  const sessionFallbackPct =
    events.length > 0 ? (fallbackCount / events.length) * 100 : 0;

  return {
    totalCostUsd,
    distinctItems,
    avgCostPerItem,
    stories,
    bugfixes,
    sessionFallbackPct,
  };
}
